/** Payment method / gateway / channel names for the admin screens — a mirror of
 *  src/core/payments/labels.py (keep in sync by hand, like lib/auth.tsx mirrors rbac.py).
 *
 *  A *method* is what the customer picks (`sbp`, `crypto`, `stars`); a *channel* is the method
 *  plus the gateway that issued the invoice: `sbp:paygatecore`, `sbp:idmpay`, `sbp:wata`, `crypto:heleket`,
 *  `stars`. Rows from the API already carry `channel_label`; these maps label the filters and
 *  anything the API sends as a bare id. Pure, no I/O. */

export const METHODS = ['sbp', 'crypto', 'stars'] as const
export const CHANNELS = ['sbp:paygatecore', 'sbp:idmpay', 'crypto:heleket', 'stars', 'sbp:wata'] as const
export const GATEWAYS = ['paygatecore', 'idmpay', 'wata', 'heleket', 'stars'] as const

export const METHOD_LABELS: Record<string, string> = { sbp: 'СБП', crypto: 'Крипта', stars: 'Stars' }

/** The gateway first, the method in brackets — never a «·» meta-string (design rule 19). */
export const CHANNEL_LABELS: Record<string, string> = {
  'sbp:paygatecore': 'PayGateCore (СБП)',
  'sbp:idmpay': 'IDMPay (СБП)',
  'sbp:wata': 'WATA (СБП)',
  'crypto:heleket': 'Heleket (крипта)',
  stars: 'Telegram Stars',
}

export const GATEWAY_LABELS: Record<string, string> = {
  paygatecore: 'PayGateCore',
  idmpay: 'IDMPay',
  wata: 'WATA',
  heleket: 'Heleket',
  stars: 'Telegram Stars',
}

const METHOD_OF_GATEWAY: Record<string, string> = {
  paygatecore: 'sbp',
  idmpay: 'sbp',
  wata: 'sbp',
  heleket: 'crypto',
  stars: 'stars',
}

/** The default (and pre-2026-09-26) СБП gateway: an СБП row without a gateway is its. */
export const DEFAULT_SBP_GATEWAY = 'paygatecore'

/** Channel id of a payment from `payments.provider` + `payments.gateway` (labels.channel_of). */
export function channelOf(provider: string, gateway: string | null | undefined): string {
  const key = (provider ?? '').trim().toLowerCase()
  if (key === 'sbp') return `sbp:${gateway || DEFAULT_SBP_GATEWAY}`
  if (key === 'heleket') return 'crypto:heleket'
  if (key === 'stars') return 'stars'
  return gateway ? `${provider}:${gateway}` : provider
}

/** Russian label of a channel id; an unknown id is shown as is (a screen never fails on it). */
export function channelLabel(channel: string | null | undefined): string {
  if (!channel) return '—'
  return CHANNEL_LABELS[channel] ?? channel
}

export function methodLabel(method: string | null | undefined): string {
  if (!method) return '—'
  return METHOD_LABELS[method] ?? method
}

export function gatewayLabel(gateway: string | null | undefined): string {
  if (!gateway) return '—'
  return GATEWAY_LABELS[gateway] ?? gateway
}

/** Gateways a filter may offer for the chosen method ('' = every method). */
export function gatewaysFor(method: string): string[] {
  if (!method) return [...GATEWAYS]
  return GATEWAYS.filter((g) => METHOD_OF_GATEWAY[g] === method)
}

/** A gateway filter that no longer fits the chosen method is dropped (СБП + Heleket = nothing). */
export function gatewayFits(method: string, gateway: string): boolean {
  if (!gateway || !method) return true
  return METHOD_OF_GATEWAY[gateway] === method
}
